// Demo 详情与 Demo-to-Task 启动流 / Demo detail and demo-to-task start flow.
// 展示只读 Demo 方案，并在登录后复制为用户 Task 再启动模拟。
// Shows read-only Demo plans and, when signed in, copies the Demo to a user Task before starting.

import { ActiveSimulationError, RateLimitedError } from "@/api/client";
import { simulationsApi } from "@/api/simulations";
import { tasksApi as defaultTasksApi } from "@/api/tasks";
import type { TaskCreateRequest, TaskSimulationStartResponse } from "@/api/tasks";
import { openAuthModal } from "@/features/auth-modal";
import { startButtonLoading } from "@/features/async-button";
import { showConfirmSimulationModal } from "@/features/confirm-simulation-modal";
import { closeDetailModal, openDetailModal } from "@/features/detail-modal";
import { showInfoModal } from "@/features/info-modal";
import { onSimulationStarted } from "@/features/log-terminal/simulation-binder";
import { setActiveTab } from "@/features/log-terminal/terminal";
import { renderCardList } from "@/features/card-list";
import { renderMarkdownToSafeHtml } from "@/lib/markdown";
import { escapeHtml } from "@/lib/dom";
import { formatConfigLabel, type ConfigKind } from "@/lib/config-labels";
import type { DemoABEntry, DemoSingleEntry, Mode, TaskDetail } from "@/lib/types";
import { state } from "@/state/store";

// Demo 详情目标 / Target Demo detail to open or start.
export interface DemoDetailTarget {
  mode: Mode;
  id: string;
}

interface DemoDetailDeps {
  tasksApi?: Pick<typeof defaultTasksApi, "create" | "startSimulation">;
  windowOpen?: (url: string, target?: string) => void;
}

const DEFAULT_DEPS: Required<DemoDetailDeps> = {
  tasksApi: defaultTasksApi,
  windowOpen: (url, target) => {
    window.open(url, target);
  },
};
let demoDetailLanguageEvents: AbortController | null = null;

/**
 * 打开只读 Demo 详情浮层 / Open the read-only Demo detail modal.
 */
export function openDemoDetail(target: DemoDetailTarget, deps: DemoDetailDeps = {}): boolean {
  const demo = findDemo(target);
  if (!demo) return false;
  const d = { ...DEFAULT_DEPS, ...deps };

  const opened = openDetailModal({
    title: demo.title || demo.id,
    dirty: false,
    render: () => renderDemoBody(target.mode, demo),
    footer: () => renderDemoFooter(target, demo, d),
  });
  if (opened) bindDemoDetailLanguageRefresh(target, d);
  return opened;
}

// 刷新已打开 Demo 浮层的动态语言标签 / Refresh dynamic labels in an open Demo modal.
function bindDemoDetailLanguageRefresh(
  target: DemoDetailTarget,
  deps: Required<DemoDetailDeps>,
): void {
  demoDetailLanguageEvents?.abort();
  demoDetailLanguageEvents = new AbortController();
  window.addEventListener("ripple:language-changed", () => {
    const root = document.getElementById("detail-modal-root");
    const body = root?.querySelector<HTMLElement>(".detail-modal-body");
    const footer = root?.querySelector<HTMLElement>(".detail-modal-footer");
    if (!root || root.hidden || !body || !root.querySelector(".demo-detail-body")) return;
    const demo = findDemo(target);
    if (!demo) return;
    body.replaceChildren(renderDemoBody(target.mode, demo));
    footer?.replaceChildren(renderDemoFooter(target, demo, deps));
  }, { signal: demoDetailLanguageEvents.signal });
}

/**
 * 从 Demo 创建用户 Task 并启动模拟 / Create a user Task from Demo and start simulation.
 */
export async function startDemoSimulation(
  target: DemoDetailTarget,
  deps: DemoDetailDeps = {},
): Promise<TaskSimulationStartResponse | null> {
  if (!state.auth?.authenticated) {
    openAuthModal("login");
    return null;
  }
  const cost = target.mode === "ab" ? 2 : 1;
  const currentCredit = state.auth.user?.credit ?? 0;
  // 启动前弹确认；credit 不足的分支由同一 modal 负责提示
  // Confirm before starting; the same modal handles the insufficient-credit path.
  const confirmed = await showConfirmSimulationModal({
    cost,
    currentCredit,
    mode: target.mode,
  });
  if (!confirmed) return null;
  // 启动前预检是否已有活跃仿真，避免多发一次必然 409 的 start 请求。
  // Preflight for an already-running simulation before hitting the API.
  if (await hasAnyRunningSimulation()) return null;
  const demo = findDemo(target);
  if (!demo) throw new Error(`Demo not found: ${target.id}`);
  const d = { ...DEFAULT_DEPS, ...deps };
  const createBody = buildTaskCreateRequest(target.mode, demo);
  const response = await d.tasksApi.create(createBody);
  let started: TaskSimulationStartResponse;
  try {
    started = await d.tasksApi.startSimulation(response.task.id);
  } catch (error) {
    if (handleDemoStartError(error)) return null;
    throw error;
  }
  // 通知终端绑定器切换到新仿真 / Notify the terminal binder about the new simulation.
  // 成功后自动切到 Plan A tab；失败由 binder 自行记录日志，UI 路径静默。
  // On success, switch to Plan A; failures are already logged by the binder.
  void onSimulationStarted(started.simulation_id, target.mode)
    .then(() => setActiveTab("planA"))
    .catch(() => { /* binder logs; silent in UI */ });
  upsertStartedTask(mergeQueuedSimulation(response.task, started));
  closeDetailModal();
  renderCardList();
  window.dispatchEvent(new CustomEvent("ripple:card-selection-changed"));
  return started;
}

// 渲染详情主体 / Render the modal body.
function renderDemoBody(mode: Mode, demo: DemoSingleEntry | DemoABEntry): HTMLElement {
  const body = document.createElement("div");
  body.className = "demo-detail-body";
  body.innerHTML = `
    <p class="demo-detail-description">${escapeHtml(demo.description || "")}</p>
    <div class="workbench-tag-row demo-detail-tags">
      ${renderConfigTags((demo.defaults ?? {}) as Record<string, unknown>)}
      <span>${mode === "ab" ? "2 Plans" : "1 Plan"}</span>
    </div>
  `;
  if (mode === "ab") {
    const abDemo = demo as DemoABEntry;
    body.append(
      renderPlanSection("Plan A", abDemo.plan_a_markdown),
      renderPlanSection("Plan B", abDemo.plan_b_markdown),
    );
  } else {
    body.append(renderPlanSection("Plan A", (demo as DemoSingleEntry).plan_markdown));
  }
  return body;
}

// 渲染详情底部按钮 / Render the modal footer buttons.
// 左侧：Close；右侧：Start Simulation（黄色）+ Open Report（次级）。
// Demo 只读，没有 Save；按钮视觉与工作台工具栏保持完全一致。
// Layout: Close on the left; Start (amber) + Open Report (secondary) on the right.
// Demo is read-only so there is no Save. Run-command buttons reuse the toolbar styling.
function renderDemoFooter(
  target: DemoDetailTarget,
  demo: DemoSingleEntry | DemoABEntry,
  deps: Required<DemoDetailDeps>,
): HTMLElement {
  const actions = document.createElement("div");
  actions.className = "demo-detail-actions detail-modal-actions";

  const left = document.createElement("div");
  left.className = "detail-modal-actions-left";

  const close = buildRunCommandButton({
    className: "demo-detail-close",
    label: "Close",
    iconKind: "close",
    secondary: true,
  });
  close.addEventListener("click", () => {
    closeDetailModal();
  });
  left.append(close);

  const right = document.createElement("div");
  right.className = "detail-modal-actions-right";

  const start = buildRunCommandButton({
    className: "demo-detail-start",
    label: "Start Simulation",
    iconKind: "start",
  });
  start.addEventListener("click", () => {
    if (start.disabled) return;
    if (!state.auth?.authenticated) {
      openAuthModal("login");
      return;
    }
    const restore = startButtonLoading(start, "Starting");
    void startDemoSimulation(target, deps)
      .catch((error) => renderDemoError(actions, error))
      .finally(restore);
  });

  const report = buildRunCommandButton({
    className: "demo-detail-open-report",
    label: "Open Report",
    iconKind: "report",
    secondary: true,
  });
  report.disabled = !demo.report_slug;
  report.addEventListener("click", () => {
    if (demo.report_slug) {
      deps.windowOpen(`/demo-reports/${encodeURIComponent(demo.report_slug)}`, "_blank");
    }
  });

  right.append(start, report);
  actions.append(left, right);
  return actions;
}

// 构造工作台风格的 run-command 按钮 / Build a toolbar-style run-command button.
// 与 index.html 里的 Start / Open Report 保持 DOM 结构一致，便于共用 CSS。
// Keeps DOM structure identical to the toolbar markup so styling lines up.
interface RunCommandButtonOptions {
  className: string;
  label: string;
  iconKind: "start" | "report" | "close";
  secondary?: boolean;
}

function buildRunCommandButton(opts: RunCommandButtonOptions): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `run-command-button ${opts.secondary ? "secondary " : ""}${opts.className}`.trim();

  const icon = document.createElement("span");
  // "start" 复用工具栏默认三角形（无修饰类），"report" 走 report 修饰类。
  // "start" reuses the toolbar triangle; "report" uses the report modifier.
  icon.className = opts.iconKind === "start"
    ? "run-command-icon"
    : `run-command-icon ${opts.iconKind}`;
  icon.setAttribute("aria-hidden", "true");

  const text = document.createElement("span");
  text.textContent = opts.label;

  button.append(icon, text);
  return button;
}

// 渲染单个 Markdown 方案 / Render one Markdown plan section.
function renderPlanSection(title: string, markdown: string): HTMLElement {
  const section = document.createElement("section");
  section.className = "demo-detail-plan";
  section.innerHTML = `
    <h3>${escapeHtml(title)}</h3>
    <div class="plan-markdown">${renderMarkdownToSafeHtml(markdown, { stripFrontmatter: true })}</div>
  `;
  return section;
}

// 构造 Task 创建请求 / Build the task-create payload.
function buildTaskCreateRequest(
  mode: Mode,
  demo: DemoSingleEntry | DemoABEntry,
): TaskCreateRequest {
  if (mode === "ab") {
    const abDemo = demo as DemoABEntry;
    return {
      mode,
      title: demo.title,
      plan_a_md: abDemo.plan_a_markdown,
      plan_b_md: abDemo.plan_b_markdown,
      config: demo.defaults,
    };
  }
  return {
    mode,
    title: demo.title,
    plan_a_md: (demo as DemoSingleEntry).plan_markdown,
    plan_b_md: null,
    config: demo.defaults,
  };
}

// 写入新 Task 到卡片状态 / Insert the newly-created Task into card state.
function upsertStartedTask(task: TaskDetail): void {
  state.workbenchView = "tasks";
  state.tasks = [task, ...state.tasks.filter((item) => item.id !== task.id)];
  state.selectedTaskId = task.id;
}

// 合并刚启动的模拟到 Task 卡片 / Merge the queued simulation into the Task card.
function mergeQueuedSimulation(
  task: TaskDetail,
  started: TaskSimulationStartResponse,
): TaskDetail {
  const now = new Date().toISOString();
  return {
    ...task,
    simulation_count: Math.max(task.simulation_count, 0) + 1,
    latest_simulation: {
      simulation_id: started.simulation_id,
      status: started.status,
      report_status: "pending",
      report_id: null,
      created_at: now,
      started_at: null,
      completed_at: null,
    },
    simulation_history: [
      {
        simulation_id: started.simulation_id,
        status: started.status,
        report_status: "pending",
        report_id: null,
        created_at: now,
        started_at: null,
        completed_at: null,
      },
      ...task.simulation_history,
    ],
  };
}

// 查找 Demo / Find a Demo by target.
function findDemo(target: DemoDetailTarget): DemoSingleEntry | DemoABEntry | null {
  const list = target.mode === "ab" ? state.demos.ab : state.demos.single;
  return list.find((demo) => demo.id === target.id) ?? null;
}

// 渲染配置标签（中文展示） / Render config tags in Chinese labels.
function renderConfigTags(config: Record<string, unknown>): string {
  return (["channel", "platform", "vertical"] as const satisfies readonly ConfigKind[])
    .map((key) => {
      const raw = String(config[key] ?? "").trim();
      if (!raw) return "";
      const label = formatConfigLabel(key, raw);
      const title = raw && raw !== label ? ` title="${escapeHtml(raw)}"` : "";
      return `<span${title}>${escapeHtml(label)}</span>`;
    })
    .filter(Boolean)
    .join("");
}

// 渲染启动错误 / Render start-flow errors.
function renderDemoError(root: HTMLElement, error: unknown): void {
  const existing = root.querySelector(".demo-detail-error");
  existing?.remove();
  const node = document.createElement("p");
  node.className = "demo-detail-error";
  node.textContent = error instanceof Error ? error.message : String(error);
  root.append(node);
}

// 启动前预检 / Preflight: detect an already-running simulation.
async function hasAnyRunningSimulation(): Promise<boolean> {
  try {
    const body = await simulationsApi.active();
    if (!body.simulation) return false;
    await showInfoModal({
      title: "Simulation already running",
      message: "Another simulation is already running. Please wait for it to finish before starting a new one.",
      tone: "info",
    });
    return true;
  } catch {
    return false;
  }
}

// 把后端已知错误转成信息弹窗 / Translate known start errors into info modals.
function handleDemoStartError(error: unknown): boolean {
  if (error instanceof ActiveSimulationError) {
    void showInfoModal({
      title: "Simulation already running",
      message: "Another simulation is already running. Please wait for it to finish before starting a new one.",
      tone: "info",
    });
    return true;
  }
  if (error instanceof RateLimitedError) {
    void showInfoModal({
      title: "Too many requests",
      message: error.message || "Please wait a moment and try again.",
      tone: "warn",
    });
    return true;
  }
  return false;
}
